import { useState, useRef, useEffect, useMemo } from "react";
import { Icon } from "@iconify/react";
import { PlusIcon } from "@heroicons/react/24/outline";
import ReportDialogModal from "../../components/modal/ReportDialogModal";
import {
  useGetReports,
  useDownloadReport,
  useDeleteReport,
} from "../../queries/admin";
import { ReportItem } from "../../types/device";
import StatsGrid from "../../components/ui/StatsGrid";

const statusConfig: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  COMPLETED: {
    label: "Completed",
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-700 dark:text-green-400",
  },
  IN_PROGRESS: {
    label: "In Progress",
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-700 dark:text-yellow-400",
  },
  STARTED: {
    label: "Started",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-700 dark:text-blue-400",
  },
  FAILED: {
    label: "Failed",
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-700 dark:text-red-400",
  },
};

function ReportActionMenu({
  report,
  onDownloadExcel,
  onDownloadPdf,
  onDelete,
}: {
  report: ReportItem;
  onDownloadExcel: () => void;
  onDownloadPdf: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <Icon
          icon="mdi:dots-vertical"
          className="w-5 h-5 text-gray-500 dark:text-gray-400"
        />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 py-1">
          {report.hasExcel && (
            <button
              onClick={() => {
                onDownloadExcel();
                setOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Icon icon="mdi:file-excel-outline" className="w-4 h-4 text-green-600" /> Download Excel
            </button>
          )}
          {report.hasPdf && (
            <button
              onClick={() => {
                onDownloadPdf();
                setOpen(false);
              }}
              className="flex items-center gap-3 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Icon icon="mdi:file-pdf-box" className="w-4 h-4 text-red-600" /> Download PDF
            </button>
          )}
          <button
            onClick={() => {
              onDelete();
              setOpen(false);
            }}
            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Icon icon="mdi:delete-outline" className="w-4 h-4" /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [showNewReport, setShowNewReport] = useState(false);
  const { data: reports = [], isLoading } = useGetReports();
  const downloadMutation = useDownloadReport();
  const deleteMutation = useDeleteReport();

  const reportStats = useMemo(() => {
    const total = reports.length;
    const completed = reports.filter((r) => r.status === "COMPLETED").length;
    const failed = reports.filter((r) => r.status === "FAILED").length;
    const totalEmailsSent = reports
      .filter((r) => r.status === "COMPLETED")
      .reduce((acc, r) => acc + (r.emails?.length || 0), 0);
    return [
      { name: "Total Reports", value: String(total) },
      { name: "Completed", value: String(completed) },
      { name: "Failed", value: String(failed) },
      { name: "Emails Sent", value: String(totalEmailsSent) },
    ];
  }, [reports]);

  // Collect recent sent emails from completed reports
  const recentEmails = useMemo(() => {
    const emailEntries: { email: string; reportName: string; date: string }[] = [];
    for (const report of reports) {
      if (report.status === "COMPLETED" && report.emails?.length > 0) {
        for (const email of report.emails) {
          emailEntries.push({
            email,
            reportName: report.device.name,
            date: report.createdAt,
          });
        }
      }
    }
    return emailEntries.slice(0, 10);
  }, [reports]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const parseDateRange = (note: string | null) => {
    if (!note) return null;
    // Match new format: "Report for X from MMM D, YYYY to MMM D, YYYY"
    const newMatch = note.match(/from (.+?) to (.+?)\./);
    if (newMatch) {
      return { from: newMatch[1], to: newMatch[2] };
    }
    // Match old format: "from <ISO> to <ISO> and"
    const oldMatch = note.match(/from (.+?) to (.+?) and/);
    if (oldMatch) {
      return {
        from: new Date(oldMatch[1]).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
        to: new Date(oldMatch[2]).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }),
      };
    }
    return null;
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Reports
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 max-w-2xl">
            Generate, download, and email sensor data reports in Excel and PDF
            formats.
          </p>
        </div>

        <button
          onClick={() => setShowNewReport(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm shrink-0"
        >
          <PlusIcon className="w-4 h-4" />
          New Report
        </button>
      </div>

      {/* Statistics */}
      <div className="mb-8">
        <StatsGrid stats={reportStats} />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Icon
            icon="mdi:loading"
            className="w-8 h-8 text-gray-400 animate-spin"
          />
        </div>
      ) : reports.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-40 text-center w-full">
          <img
            src="/sidenav/complaince.svg"
            alt="No reports"
            className="w-16 h-16 opacity-40 dark:invert mx-auto mb-4"
          />
          <p className="text-lg font-medium text-gray-500 dark:text-gray-400">
            No reports
          </p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Click "New Report" to generate one.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Previous Reports */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Previous Reports
            </h2>
            <div className="space-y-3">
              {reports.map((report) => {
                const status =
                  statusConfig[report.status] ?? statusConfig.STARTED;
                const dateRange = parseDateRange(report.note);
                return (
                  <div
                    key={report.id}
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-6 py-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon
                            icon="mdi:file-document-outline"
                            className="w-5 h-5 text-gray-500 dark:text-gray-400"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            Report - {report.device.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(report.createdAt)}
                            {dateRange && (
                              <>
                                {" "}
                                &middot; {dateRange.from} to {dateRange.to}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span
                          className={`text-xs font-medium px-2.5 py-1 rounded-full ${status.bg} ${status.text}`}
                        >
                          {status.label}
                        </span>
                        <ReportActionMenu
                          report={report}
                          onDownloadExcel={() =>
                            downloadMutation.mutate({
                              jobId: report.id,
                              format: "xlsx",
                            })
                          }
                          onDownloadPdf={() =>
                            downloadMutation.mutate({
                              jobId: report.id,
                              format: "pdf",
                            })
                          }
                          onDelete={() => deleteMutation.mutate(report.id)}
                        />
                      </div>
                    </div>
                    {/* Email recipients */}
                    {report.emails && report.emails.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                        <div className="flex items-center gap-2">
                          <Icon
                            icon="mdi:email-outline"
                            className="w-3.5 h-3.5 text-gray-400"
                          />
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Sent to:{" "}
                          </span>
                          <div className="flex flex-wrap gap-1.5">
                            {report.emails.map((email) => (
                              <span
                                key={email}
                                className="text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 px-2 py-0.5 rounded-full"
                              >
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Last Sent Emails Sidebar */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recent Email Deliveries
            </h2>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
              {recentEmails.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <Icon
                    icon="mdi:email-outline"
                    className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-2"
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No emails sent yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {recentEmails.map((entry, idx) => (
                    <div key={idx} className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon
                            icon="mdi:email-check-outline"
                            className="w-4 h-4 text-blue-600 dark:text-blue-400"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-900 dark:text-white truncate">
                            {entry.email}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {entry.reportName} &middot;{" "}
                            {formatDateTime(entry.date)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Report Modal */}
      <ReportDialogModal
        isOpen={showNewReport}
        onClose={() => setShowNewReport(false)}
      />
    </div>
  );
}
